import { supabase } from "@/integrations/supabase/client";
import type { LearningEngineDecision } from "./types";
import { SAFE_LOCKED_DECISION } from "./engineRules";

function normalizeDecision(raw: unknown): LearningEngineDecision {
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (!row || typeof row !== "object") return SAFE_LOCKED_DECISION;
  const d = row as Partial<LearningEngineDecision>;
  return {
    action: (d.action as LearningEngineDecision["action"]) ?? SAFE_LOCKED_DECISION.action,
    reason: d.reason ?? SAFE_LOCKED_DECISION.reason,
    target_module_key: d.target_module_key ?? null,
    target_lesson_id: d.target_lesson_id ?? null,
    target_route: d.target_route ?? "/dashboard",
    is_locked: Boolean(d.is_locked),
    lock_reason: d.lock_reason ?? null,
    support_level: (d.support_level as LearningEngineDecision["support_level"]) ?? "normal",
    reward_impact: (d.reward_impact as LearningEngineDecision["reward_impact"]) ?? "none",
    audit_code: d.audit_code ?? "MISSING_AUDIT_CODE",
    metadata: (d.metadata as Record<string, unknown>) ?? {},
  };
}

/**
 * Calls the server-authoritative resolver. On any failure returns the
 * safe-locked decision so the client never fabricates an unlocked state.
 */
export async function resolveNextLearningAction(): Promise<LearningEngineDecision> {
  const { data: userRes, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userRes?.user) return SAFE_LOCKED_DECISION;

  const { data, error } = await (supabase).rpc("resolve_next_learning_action", {
    p_user_id: userRes.user.id,
  });

  if (error) {
    console.error("[learning-engine] resolve_next_learning_action failed", error);
    return {
      ...SAFE_LOCKED_DECISION,
      metadata: { errorMessage: error.message },
    };
  }
  return normalizeDecision(data);
}